import { AxiosError, AxiosInstance } from "axios";
import { compact } from "lodash";
import { useMemo, useState } from "react";
import { InfiniteData, QueryKey, useInfiniteQuery } from "react-query";
import { PlaceholderDataFunction, UseInfiniteQueryOptions } from "react-query/types";
import { api as xolaApi } from "../api/api";
import { queryClient } from "../api/queryClient";
import { Resource, Response } from "../types/Response";
import { flattenInfiniteData, generateQueryKey, Page, populateCache, populateResource } from "./helper";

export interface Options<T> extends Omit<UseInfiniteQueryOptions<any, AxiosError, Page<T>>, "placeholderData"> {
    apiClient?: AxiosInstance;
    queryKey?: QueryKey;
    // Since this is useCollection, we expect placeHolderData to have or return an array of items
    placeholderData?: InfiniteData<T> | PlaceholderDataFunction<InfiniteData<T>>;
    queryItemLimit?: number; // Limits the number or items to be fetched
    shouldPopulateCache?: boolean;
}

export const getNextPageParam = <T>(lastPage: Page<T>) => {
    if (Array.isArray(lastPage.next)) {
        return lastPage.next.map((url) => url.replace(/^\/api/, ""));
    }

    return lastPage.next?.replace(/^\/api/, "");
};

/**
 * Uses `useInfiniteQuery` hook along with `axios` client to fetch API collections for infinitely loading lists.
 * Expects that the response has a `paging.next` property containing URL for the next page of results.
 */
export const useInfiniteCollection = <T extends Resource>(
    path: string | string[],
    queryParams?: any,
    options?: Options<T>,
) => {
    const api = options?.apiClient ?? xolaApi;
    const [defaultData] = useState<T[]>([]);
    const [collectionName] = Array.isArray(path) ? path.slice(-1) : [path];
    const params = Array.isArray(queryParams)
        ? queryParams.map((param) => ({ limit: 100, ...param }))
        : { limit: 100, ...queryParams };

    const queryKey = options?.queryKey ?? generateQueryKey([collectionName, "collection", "infinite"], params);
    const queryItemLimit = options?.queryItemLimit;

    const query = useInfiniteQuery<any, AxiosError, Page<T>>(
        queryKey,
        async ({ pageParam: nextUrl }) => {
            const url = Array.isArray(path) ? path.join("/") : path;

            // Added the cap of fetched items to prevent cache from growing too large as feedback the product meeting April 15
            const fetchedQueryItems = query.data?.pages.flatMap((page) => page.data) ?? [];

            if (queryItemLimit && fetchedQueryItems.length >= queryItemLimit) {
                return {
                    data: [],
                    next: null,
                    isQueryAbortedDueToCacheLimit: true,
                };
            }

            const createRequest = async (url: string, params: any) => api.get<Response<T, Resource>>(url, { params });

            // Create requests for each URL or params, can be single or multiple params, or URLs with nextPages from the previous response/s

            const requestParamsItems = nextUrl ?? params;
            const requests = requestParamsItems
                ? Array.isArray(requestParamsItems)
                    ? requestParamsItems.map(async (urlOrParam: string) => {
                          // This is to handle the case where the nextUrl is an array of URLs
                          // Can be either initial batch of requests or nextPages from the previous response

                          const requestUrl = nextUrl ? urlOrParam : `/${url}`;
                          const requestParams = nextUrl ? {} : urlOrParam;
                          return createRequest(requestUrl, requestParams);
                      })
                    : [createRequest(nextUrl || `/${url}`, nextUrl ? {} : params)] // Single request mode
                : [];

            // Running all requests in parallel (important for nextPages to maintain control over the react-query flags)
            const responses = await Promise.all(requests);

            // Combine the data from all responses
            const combinedData: T[] = [];
            const combinedNextPages: string[] = [];
            let nextPageUrl: string | null = null;
            // By default populate the cache, incase few cases we don't want to populate the cache (like /sellers, which will override seller data)
            const shouldPopulateCache = options?.shouldPopulateCache ?? true;

            for (const response of responses) {
                const { data } = response;
                const collection: T[] = Array.isArray(data)
                    ? data
                    : "data" in data
                    ? data.data
                    : ([data] as unknown as T[]);

                combinedData.push(...collection);

                for (const resource of collection) {
                    // Avoid creating a query for each timeslot,
                    // since there can be up to 9k slots per month in the worst case (every 5 mins).
                    if (resource?.id && collectionName !== "timeslots" && shouldPopulateCache) {
                        populateResource(collectionName, resource);
                    }

                    // If we already created a cache query for a single timeslot,
                    // we need to rewrite it to have fresh data, since we can't just invalidate single timeslot.
                    if (collectionName === "timeslots") {
                        const existingSlotCache = queryClient.getQueryData(["timeslots", resource?.id]);

                        if (existingSlotCache) {
                            queryClient.setQueryData(["timeslots", resource?.id], resource);
                        }
                    }
                }

                if (shouldPopulateCache) {
                    populateCache(data, params);
                }

                // Update the next page URL if available
                if (data.paging?.next) {
                    combinedNextPages.push(data.paging.next);
                    nextPageUrl = data.paging.next;
                }
            }

            return {
                data: combinedData,
                next: combinedNextPages.length > 1 ? combinedNextPages : nextPageUrl,
            };
        },
        {
            getNextPageParam,
            ...options,
        },
    );

    const data = useMemo(() => {
        return flattenInfiniteData(query.data, defaultData);
    }, [query.data, defaultData]);

    const hasMore = query.hasNextPage ?? false;
    const isLoadingPage = query.isLoading || query.isFetchingNextPage;

    interface ExtendedPage<T> extends Page<T> {
        isQueryAbortedDueToCacheLimit?: boolean;
    }

    // variable if query ended due to cache limit
    const isQueryAbortedDueToCacheLimit =
        query.data?.pages.some(
            (page: ExtendedPage<T>) => "isQueryAbortedDueToCacheLimit" in page && page.isQueryAbortedDueToCacheLimit,
        ) ?? false;

    return { ...query, hasMore, isLoadingPage, data, isQueryAbortedDueToCacheLimit };
};

// This checks if there is data for the given collection without any real filters
export const useHasBaseCollectionData = (collectionName: string, options: any) => {
    const queryKey = compact([collectionName, "collection", "infinite", options]);
    const queryData: any = queryClient.getQueryData(queryKey);
    if (!queryData?.pages) {
        return false;
    }

    const data = queryData?.pages.flatMap((page: any) => page.data) ?? [];
    return data.length > 1;
};
